/**
 * TG-A / TG-B — pure sample buffer for transaction progress telemetry
 * (power · SoC · cumulative energy vs time). Lives in `lib` (a leaf module) so
 * both the UI chart and the state-layer sampling sink can import it.
 *
 * The buffer spans the WHOLE transaction; once it exceeds `MAX_SAMPLES` it is
 * decimated (every other point dropped) so it keeps covering the full span at
 * half resolution instead of growing without bound or sliding off the start.
 * Because the live buffer is already bounded, it is persisted as-is for the
 * transaction-history graph (TG-B) — no separate downsample step.
 */
export interface TxSample {
  /** Epoch ms. */
  readonly t: number;
  /** Effective charging power, W. */
  readonly powerW: number;
  /** State of charge %, or null on AC / unbound connectors. */
  readonly socPct: number | null;
  /** Cumulative energy delivered this tx, Wh. */
  readonly energyWh: number;
}

/** Upper bound on retained points; beyond this the series is decimated. */
export const MAX_SAMPLES = 600;

/**
 * Append a sample; if the buffer would exceed `max`, halve its resolution
 * (keep every other point) while always preserving the newest point, so the
 * series still spans tx-start → now.
 */
export function appendTxSample(
  prev: readonly TxSample[],
  sample: TxSample,
  max: number = MAX_SAMPLES,
): readonly TxSample[] {
  const next = [...prev, sample];
  if (next.length <= max) return next;
  const decimated: TxSample[] = [];
  for (let i = 0; i < next.length; i += 2) {
    const s = next[i];
    if (s !== undefined) decimated.push(s);
  }
  const last = next[next.length - 1];
  if (last !== undefined && decimated[decimated.length - 1] !== last) {
    decimated.push(last);
  }
  return decimated;
}

/** Largest value across the series for `key` (≥ 1 so callers can divide safely). */
export function seriesMax(samples: readonly TxSample[], key: 'powerW' | 'energyWh'): number {
  let m = 0;
  for (const s of samples) {
    const v = s[key];
    if (v > m) m = v;
  }
  return m > 0 ? m : 1;
}

/** True when at least one sample carries a non-null SoC (DC sessions). */
export function hasSoc(samples: readonly TxSample[]): boolean {
  return samples.some((s) => s.socPct !== null);
}
